import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { showErrorToast } from "@/components/toster";
import { handleApiError } from "@/config/errorhandler";

import { ApiError } from "./apiError";

/**
 * The application's ONE QueryClient.
 *
 * A module singleton rather than a `useMemo` inside a component: `logout()` in
 * `AuthContext` has to be able to wipe the cache, and it is called from nine
 * different places (including `httpService`, which is not a React component).
 * A singleton is reachable from all of them.
 *
 * ── Cache tiers ────────────────────────────────────────────────────────────
 * The defaults below are TIER C ("metric data") — the workhorse settings that
 * most queries want. Queries that need something else set `staleTime`/`gcTime`
 * explicitly via the constants in `cacheTiers`.
 */

/** Per-module cache settings. See `docs/tanstack-query-integration-plan.md` §4. */
export const cacheTiers = {
  /** Bootstrap config — branches, payors, service lines, role grants. Changes
   *  only by admin action, so it is fetched once and held for the session. */
  bootstrap: { staleTime: 30 * 60 * 1000, gcTime: 60 * 60 * 1000 },
  /**
   * Saved user preferences (`dashboard/custom-views`).
   *
   * `staleTime: 0` is DELIBERATE and is the one place the app opts out of
   * caching: a dashboard's saved filter selections must be re-read from the
   * server every time that dashboard is opened, so the filter bar always
   * pre-selects what is actually stored and the metric calls that follow carry
   * it. Caching this would mean opening a dashboard with a filter set that a
   * later save (here or elsewhere) had already replaced.
   *
   * The entry is still CACHED (`gcTime`), it is just always revalidated: the
   * previous response renders the views picker instantly while the fresh one is
   * in flight, so nothing flashes empty.
   */
  prefs: { staleTime: 0, gcTime: 30 * 60 * 1000 },
  /**
   * Metric data. The warehouse behind these SPs refreshes on a schedule (it is
   * surfaced as `config.dataRefreshDate`), not continuously, so a 2-hour window
   * covers a full working session — dashboard switching, filter tweaking,
   * drill-downs — with no refetch, while a user returning the next morning still
   * gets the new day's numbers.
   *
   * `gcTime` MUST NOT be shorter than `staleTime`. Its timer starts when a query
   * loses its last observer, and `key={s.pillar}` unmounts `PillarPage` on every
   * dashboard switch — so a shorter `gcTime` would evict the entry while it was
   * still considered fresh, and the longer `staleTime` would buy nothing beyond
   * that window for any dashboard the user had left.
   */
  metrics: { staleTime: 2 * 60 * 60 * 1000, gcTime: 2 * 60 * 60 * 1000 },
  /** Record-level drill-downs — modal-scoped, potentially large, lower revisit
   *  rate, so a shorter gcTime caps memory. */
  records:  { staleTime: 2 * 60 * 60 * 1000, gcTime: 2 * 60 * 60 * 1000 },
  /**
   * Charlie — the Administrator Daily Brief (feed + signal drill-downs).
   *
   * Shorter than the metric tiers on purpose: these are TASKS, not warehouse
   * numbers. They are raised by the signal job and are acted on by people
   * during the session — including by this user, in another tab — so a 2-hour
   * window would leave the brief showing work that is already done. Fifteen
   * minutes still collapses the burst that matters: re-opening the same drill,
   * and hopping between service lines and branches and back.
   *
   * A write invalidates the feed regardless (see `charlieApi.ts`), so this
   * window never holds a list the user's own decisions have moved on from.
   */
  charlie: { staleTime: 15 * 60 * 1000, gcTime: 30 * 60 * 1000 },
} as const;

/**
 * Decide whether a failure should raise a toast, reproducing EXACTLY what the
 * hand-rolled hooks did before the migration.
 *
 * They split failures two ways, and the split was NOT uniform:
 *
 *   • An HTTP failure (status >= 400) — which the axios interceptor resolves
 *     rather than rejects — was toasted by only TWO hooks, `useDashboardConfig`
 *     and `useDashboardMetrics` (`if (toast) showErrorToast(message)`). Every
 *     other hook used `apiErrorMessage` and rendered the message inline in its
 *     own section, silently. `useMetricRelations` documents this explicitly:
 *     "Silent on failure — the Relationship section is supplementary."
 *
 *     → Toasting is therefore OPT-IN, via `meta: { toastOnError: true }`.
 *
 *   • A thrown exception (network failure, offline, session expiry) reached
 *     every hook's `catch` and went through `handleApiError`, which toasts and
 *     owns the de-duplicated "Session expired" message.
 *
 *     → Those still go through `handleApiError`, everywhere.
 */
function reportError(error: unknown, toastOnError: boolean): void {
  if (error instanceof ApiError) {
    // An HTTP failure. Inline-only unless the hook opted in.
    if (!toastOnError) return;
    // `apiErrorInfo` marks expected empty states (e.g. a dashboard whose
    // metrics are all disabled) as no-toast — they get the inline message only.
    if (!error.shouldToast) return;
    showErrorToast(error.message);
    return;
  }
  // A thrown exception. `handleApiError` special-cases "Session expired" so the
  // message appears once, not once per in-flight request.
  handleApiError(error);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // NOTE: TanStack Query v5 removed `onError` from `useQuery`, so all
    // cross-cutting query error handling lives here.
    onError: (error, query) => {
      // A background refetch that fails while valid data is still on screen must
      // not toast — the user is looking at good data and nothing has broken for
      // them. (Before the migration there was no cache, so this case could not
      // arise: a failed request always blanked the section.)
      if (query.state.data !== undefined) return;
      reportError(error, query.meta?.toastOnError === true);
    },
  }),
  mutationCache: new MutationCache({
    // Matches the writes' previous behaviour: `useSaveCustomView` and
    // `useDeleteCustomView` returned false on an HTTP failure without toasting,
    // and ran thrown exceptions through `handleApiError`.
    onError: (error) => reportError(error, false),
  }),
  defaultOptions: {
    queries: {
      ...cacheTiers.metrics,
      // Nothing in this app refetched on focus before the migration. Leaving
      // this at its `true` default would fire a request burst every time a user
      // alt-tabs back — a visible behaviour change against daily-refreshed
      // warehouse data that gains nothing.
      refetchOnWindowFocus: false,
      // Safe and strictly better than before: only queries already past their
      // staleTime revalidate when the connection returns.
      refetchOnReconnect: true,
      // Default. A mount refetches ONLY if the entry is stale — within
      // staleTime it is served from cache, which is the point of the migration.
      refetchOnMount: true,
      refetchInterval: false,
      // Deliberately 0: no hook retried before the migration, so this keeps
      // behaviour provably identical. See the plan §4.3 for the retry policy to
      // enable as a separate, separately-verifiable change.
      retry: 0,
      // Section-level failures must stay section-level — a failed drill summary
      // must not blank the dashboard behind it. Every consumer already renders
      // its own inline error state.
      throwOnError: false,
    },
    mutations: {
      // Permanent. `save-custom-view` / `delete-custom-view` are not idempotent.
      retry: 0,
    },
  },
});

/**
 * Drop every cached response and cancel anything in flight.
 *
 * Called on logout, and on a login that follows a DIFFERENT user in the same
 * tab. `clear()` — not `invalidateQueries()`: invalidation marks entries stale
 * but keeps the data in memory, where the next mount would re-serve it.
 */
export function clearQueryCache(): void {
  queryClient.cancelQueries();
  queryClient.clear();
}
